/**
 * (From https://www.instagram.com/p/BxuwJfBH_7T/)
 * Create a promise, which resolves (to undefined) after a delay
 * @param {number} delayMs Delay of resolution in milliseconds
 * @param {T} resolveValue Value to resolve promise with
 */
export const timeout = <T>(delayMs: number, resolveValue?: T): Promise<T> => {
    return new Promise((resolve) => {
        window.setTimeout(resolve, delayMs, resolveValue);
    });
};

interface LeaseStatus {
    /**
     * Whether timer was successfully leased
     */
    success: boolean;

    /**
     * Whether timer is ready
     */
    done: boolean | Promise<boolean>;
}


/**
 * Provides a single timer which can be "leased out" for a duration.
 */
export class LeasedTimer {
    _timerId?: number;
    /**
     * Lease the timer. Attempts to lease while already leased fail.
     * Lease expires when timer elapses.
     * @param delayMs Milliseconds until resolution
     * @returns {LeaseStatus} Status of leased timer
     */
    lease(delayMs: number): LeaseStatus {
        if (this._timerId) {
            console.warn('Failed to lease timer (already leased)');
            return { success: false, done: false } ;
        }

        const done = new Promise<boolean>((resolve) => {
            // Must assign timer, or the leasing is irrelevant
            this._timerId = window.setTimeout(() => {
                this._timerId = undefined;
                resolve(true);
            }, delayMs);
        });
        return { success: true, done };
    }
}

/**
 * Perform a specified check at an interval until the check
 * is satisfied or the number of attempts is exhausted
 * @param isSatisfied Implementation of check
 * @param checkIntervalMs Check interval in ms
 * @param attempts
 * @returns {boolean} True if satisfied, false if exhausted
 */
export const checkUntilSatisfiedOrExhausted = async (
    isSatisfied: () => boolean,
    checkIntervalMs = 200,
    attempts = 5
): Promise<boolean> => {
    while (attempts-- > 0) {
        if (isSatisfied()) {
            return true;
        }
        await timeout(checkIntervalMs);
    }
    return false; // Exhausted
};



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/util/promise.ts